import React, { HTMLAttributes, MouseEventHandler, ReactNode } from "react";
import { CircleCheck, CircleX, Info, TriangleAlert } from "lucide-react";
import { MGMicrotext } from "../MGMicrotext";
import styles from "./MGNote.module.css";

export type TMGNoteBackgroundColor = "white" | "transparent" | "grey" | "dark";
export type TMGNoteIcon = "info" | "warning" | "success" | "error";
export type TMGNoteLink = {
  href: string;
  target?: string;
  text: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
};
export type TMGNoteLinkMaxTwoArray<T> = [T?, T?];
export type TMGNoteProps = Omit<HTMLAttributes<HTMLDivElement>, "children"> & {
  backgroundColor?: TMGNoteBackgroundColor;
  dataTestId?: string;
  description: ReactNode;
  icon?: TMGNoteIcon;
  links?: TMGNoteLinkMaxTwoArray<TMGNoteLink>;
};

const noteIcons = { info: Info, warning: TriangleAlert, success: CircleCheck, error: CircleX };

export const MGNote = ({
  backgroundColor = "white",
  className,
  dataTestId,
  description,
  icon,
  links = [],
  title,
  role = "note",
  ...htmlProps
}: TMGNoteProps) => {
  const Icon = icon ? noteIcons[icon] : undefined;
  const textColor = backgroundColor === "dark" ? "text-primary-04" : "text-primary-02";
  const visibleLinks = links.filter((link): link is TMGNoteLink => link != null).slice(0, 2);

  return (
    <div
      {...htmlProps}
      role={role}
      className={`${styles.note}${className ? ` ${className}` : ""}`}
      data-background={backgroundColor}
      data-testid={dataTestId}>
      {Icon && (
        <Icon
          className={styles.icon}
          size={24}
          aria-hidden="true"
          focusable="false"
          data-note-icon={icon}
        />
      )}
      <div className={styles.content}>
        {title && (
          <MGMicrotext
            microtextStyleDefinition="microcopy-100-06"
            color={textColor}
            className={styles.title}>
            {title}
          </MGMicrotext>
        )}
        <MGMicrotext
          microtextStyleDefinition="microcopy-100-03"
          color={textColor}
          className={styles.description}>
          {description}
        </MGMicrotext>
        {visibleLinks.length > 0 && (
          <div className={styles.links}>
            {visibleLinks.map(({ text, href, target, onClick }, index) => (
              <a
                className={styles.link}
                key={`${href}-${index}`}
                href={href}
                target={target}
                rel={target === "_blank" ? "noopener noreferrer" : undefined}
                onClick={onClick}>
                {text}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
